import { createSelector } from 'reselect';
import { isRoleAdministrator } from '../auth/selectors';
import { getSavedMeterId } from '../app/selector';
import moment from 'moment';

const getList = state => {
    if (state.meters.get('list')) {
        return state.meters.getIn(['list', 'data']);
    }
    return undefined;
};

export const getMetersList = createSelector([getList], metersListData => metersListData);

export const getValidCloudPlan = createSelector([getList, getSavedMeterId], (metersListData, meterId) => {
    if(!metersListData) return 'B';
    const meterList = metersListData.toJS ? metersListData.toJS() : [];
    const meters = meterList.filter(item => item.id === meterId);
    if(meters.length !== 1) return 'B';
    const meter = meters[0];

    if(
        !meter ||
        !meter.subscriptionUntil ||
        !meter.subscriptionType ||
        meter.subscriptionType === 'B' ||
        meter.subscriptionUntil < moment().valueOf()
    ) return 'B'; // Basic
    return meter.subscriptionType;
});

export const getSelectedMeter = createSelector([getList, getSavedMeterId], (metersListData, meterId) => {
    if(!metersListData) return undefined;
    const meterList = metersListData.toJS ? metersListData.toJS() : [];
    const meters = meterList.filter(item => item.id === meterId);
    return meters[0];
});

const getSortedMetersList = (metersListData, isAdmin) => {
    if (!(metersListData && metersListData.size > 0)) return undefined;

        const metersListDataSorted = metersListData;

        // HELPER FUNCTION FOR SORTING HOME BY NAME
        const sortByHomeNameDiff = (meterA, meterB) => {
            const homeNameDiff =
                meterA.get('home') && meterB.get('home')
                    ? (meterA.getIn(['home', 'name']) > meterB.getIn(['home', 'name'])) -
                      (meterA.getIn(['home', 'name']) < meterB.getIn(['home', 'name']))
                    : 0;

            return homeNameDiff === 0
                ? (meterA.get('name').toLowerCase() > meterB.get('name').toLowerCase()) -
                      (meterA.get('name').toLowerCase() < meterB.get('name').toLowerCase())
                : homeNameDiff;
        };
        if (isAdmin) {
            return metersListDataSorted.sort((meterA, meterB) => {
                const meterAOwnerFullName =
                    meterA.get('home') && meterA.getIn(['home', 'ownerUser'])
                        ? `${meterA.getIn(['home', 'ownerUser', 'surename']) || ''} ${meterA.getIn([
                              'home',
                              'ownerUser',
                              'name'
                          ]) || ''}`
                        : '';

                const meterBOwnerFullName =
                    meterB.get('home') && meterB.getIn(['home', 'ownerUser'])
                        ? `${meterB.getIn(['home', 'ownerUser', 'surename']) || ''} ${meterB.getIn([
                              'home',
                              'ownerUser',
                              'name'
                          ]) || ''}`
                        : '';

                const fullNameDiff =
                    (meterAOwnerFullName > meterBOwnerFullName) -
                    (meterAOwnerFullName < meterBOwnerFullName);
                return fullNameDiff === 0 ? sortByHomeNameDiff(meterA, meterB) : fullNameDiff;
            });
        }

        return metersListDataSorted.sort((meterA, meterB) => sortByHomeNameDiff(meterA, meterB));
}

export const getMetersListSorted = createSelector(
    [getList, isRoleAdministrator],
    (metersListData, isAdmin) => getSortedMetersList(metersListData, isAdmin));

const getTypesLoading = state => {
    if (state.meters.get('types')) {
        return state.meters.getIn(['types', 'isLoading']);
    }
    return false;
};

const getTypes = state => {
    if (state.meters.get('types')) {
        return state.meters.getIn(['types', 'data']);
    }
    return [];
};

export const getMeterTypes = createSelector([getTypes], meterTypesData => meterTypesData);

export const getMeterTypesIsLoading = createSelector([getTypesLoading], isLoading => isLoading);

const getPurposesLoading = state => {
    if (state.meters.get('purposes')) {
        return state.meters.getIn(['purposes', 'isLoading']);
    }
    return false;
};

const getPurposes = state => {
    if (state.meters.get('purposes')) {
        return state.meters.getIn(['purposes', 'data']);
    }
    return [];
};

export const getMeterPurposes = createSelector([getPurposes], meterTypesData => meterTypesData);

export const getMeterPurposesIsLoading = createSelector([getPurposesLoading], isLoading => isLoading);

const getModbusFlavorsLoading = state => {
    if (state.meters.get('flavors')) {
        return state.meters.getIn(['flavors', 'isLoading']);
    }
    return false;
};

const getFlavors = state => {
    if (state.meters.get('flavors')) {
        return state.meters.getIn(['flavors', 'data']);
    }
    return [];
};

export const getModbusFlavors = createSelector([getFlavors], meterTypesData => meterTypesData);

export const getModbusFlavorsIsLoading = createSelector([getModbusFlavorsLoading], isLoading => isLoading);

export const getMetersListSortedForMonitoring = createSelector(
    [getList, isRoleAdministrator],
    (metersListData, isAdmin) => {

        if (metersListData === undefined || metersListData === null)
        {
            return metersListData;
        }

        const gridAndUsageMeters = metersListData.filter(meter => meter.get('purpose') !== undefined && 
        (meter.get('purpose').toLowerCase() === "grid" || meter.get('purpose').toLowerCase() === "usage"));

        return getSortedMetersList(gridAndUsageMeters, isAdmin);
    });
